import type { QueryClient } from '@tanstack/react-query'
import type { RAccountDetails } from 'v2/pages/billing/types'
import { billingQueryKeys, getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RUserProfile } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'

// The flow reads conversation_custom_data by these exact key names, so a renamed or re-cased
// key is silently unknown to it. The mixed snake/camel casing is the flow's.
export interface BuddyFlowCustomData {
  aom_uuid: string
  auth_id: string
  email?: string
  name?: string
  plan?: string
  balance?: number
  currency?: string
  conversionRate?: number
  // Joined because the widget's config sanitizer turns arrays into index-keyed objects.
  roles?: string
  region?: string
  countryId?: string
  registeredIp?: string
  signupDate?: string
  page_url: string
}

// Reads only what the console already holds (session + query cache), as the Buddy panel does.
export const buildBuddyFlowCustomData = (queryClient: QueryClient): BuddyFlowCustomData => {
  const auth = getLocalAuth()
  const profile = queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)
  const accountId = Number(profile?.organizations?.[0]?.org_plivo_account_id)
  const account = queryClient.getQueryData<RAccountDetails>(
    billingQueryKeys().accountDetails(accountId),
  )?.account
  const balance = queryClient.getQueryData<{ amount?: number }>(
    billingQueryKeys().balance(accountId, account?.conversion_rate || 1),
  )

  return {
    aom_uuid: auth?.aom_uuid ?? '',
    auth_id: account?.auth_id || profile?.organizations?.[0]?.org_auth_id || '',
    email: auth?.email,
    name: auth?.name,
    plan: profile?.plan,
    balance: balance?.amount,
    currency: account?.currency,
    conversionRate: account?.conversion_rate,
    roles: auth?.roles?.join(', '),
    region: auth?.region,
    countryId: account?.country_id,
    registeredIp: account?.registered_ip,
    signupDate: account?.created,
    page_url: window.location.href,
  }
}
